package com.tovtalk.data.cache

/**
 * Derives a STABLE cache key from a (possibly signed) media URL.
 *
 * The same underlying S3 object can be served behind many different signed URLs that differ only
 * in their AWS signature query parameters (`X-Amz-*`, `Signature`, `Expires`). Those rotate on
 * every re-sign, so keying a cache on the raw URL causes duplicate downloads / cache misses.
 *
 * `resolve()` strips ONLY the signature parameters while preserving scheme + host + path and any
 * meaningful (non-signature) query params, so two re-signed URLs for the same object collapse to
 * one key — without colliding distinct objects that merely share a path on different hosts.
 *
 * NOTE: This is consumed by multiple caches across platforms (Coil memory/disk keys on
 * Android/iOS, SessionVideoCache, and the jsMain MediaCacheManager) purely as an opaque, stable
 * key. Keep the output deterministic and collision-free.
 */
object S3CacheKeyResolver {

    /** AWS SigV4 / SigV2 signature query-parameter names that must be dropped from the key. */
    private val signatureParamNames = setOf(
        "Signature",
        "Expires",
        "AWSAccessKeyId",
    )

    private fun isSignatureParam(name: String): Boolean =
        name.startsWith("X-Amz-", ignoreCase = true) || signatureParamNames.contains(name)

    /**
     * True when [url] carries AWS signature query parameters. We look for the actual signature
     * params (`X-Amz-Signature` / `Signature=`) rather than any substring, so e.g. a path that
     * merely contains the word "Signature" is not mistaken for a signed URL.
     */
    fun isSignedUrl(url: String): Boolean {
        val query = url.substringAfter('?', "")
        if (query.isEmpty()) return false
        return query.split('&').any { param ->
            val name = param.substringBefore('=')
            name.equals("X-Amz-Signature", ignoreCase = true) || name == "Signature"
        }
    }

    /**
     * Returns a stable cache key: scheme + host + path, plus any meaningful (non-signature) query
     * params re-appended in a deterministic (sorted) order. Drops ONLY the AWS signature params so
     * re-signed URLs for the same object map to the same key.
     *
     * Non-URL / unparseable strings are returned unchanged.
     */
    fun resolve(url: String): String {
        val queryIndex = url.indexOf('?')
        if (queryIndex < 0) return url // no query → already stable

        val base = url.substring(0, queryIndex)       // scheme + host + path
        val query = url.substring(queryIndex + 1)
        if (query.isEmpty()) return base

        // Keep only meaningful params; drop signature params. Sort for deterministic ordering so the
        // same set of params always yields the same key regardless of source ordering.
        val meaningful = query.split('&')
            .filter { it.isNotEmpty() }
            .filterNot { isSignatureParam(it.substringBefore('=')) }
            .sorted()

        return if (meaningful.isEmpty()) base else "$base?${meaningful.joinToString("&")}"
    }
}
